import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  IdentityApi,
  SignInPageProps,
  useApi,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { LandingPage } from '../LangingPage/LandingPage';
import { Root } from '../Root';
import { logtoOIDCAuthApiRef } from '../../apis';
import ContactUsPage from '../ContactUs/ContactUsPage';
import { useOpenLogin } from './useOpenLogin';

export function LandingSignInPage({ onSignInSuccess }: SignInPageProps) {
  const location = useLocation();
  const isContactUsRoute = location.pathname.startsWith('/contact-us');
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  const logtoAuthApi = useApi(logtoOIDCAuthApiRef);

  const handleSignInSuccess = useCallback(
    (identityApi: IdentityApi) => {
      onSignInSuccess(identityApi);
    },
    [onSignInSuccess],
  );

  const openLogin = useOpenLogin(handleSignInSuccess);

  useEffect(() => {
    let isMounted = true;
    const restoreSession = async () => {
      try {
        const identityResponse = await logtoAuthApi.getBackstageIdentity({
          optional: true,
        });
        if (!identityResponse || !isMounted) {
          return;
        }
        const profile = await logtoAuthApi.getProfile();
        if (!isMounted) {
          return;
        }
        handleSignInSuccess(
          UserIdentity.create({
            identity: identityResponse.identity,
            authApi: logtoAuthApi,
            profile,
          }),
        );
      } finally {
        if (isMounted) {
          setIsRestoringSession(false);
        }
      }
    };

    restoreSession();
    return () => {
      isMounted = false;
    };
  }, [handleSignInSuccess, logtoAuthApi]);

  if (isRestoringSession) {
    return <div style={{ width: '100%', minHeight: '100vh' }} />;
  }

  const content = isContactUsRoute ? (
    <ContactUsPage />
  ) : (
    <LandingPage onLoginClick={openLogin} />
  );

  return <Root onOpenLogin={openLogin}>{content}</Root>;
}
